/*
 * 브라우저(비운영)에서 Electron preload 브리지(window.electron.ipcRenderer)를 대신하는 postMessage 브리지.
 * 예약시트를 window.open 으로 띄운 React 창(opener)이 Electron 메인 프로세스 역할을 한다.
 * 채널명·payload 모양은 Electron IPC 와 동일하게 유지한다.
 * 프로토콜 상대 구현(source·채널·envelope 동기화 필요): AfterDoc-React/apps/services/afterdoc-saas-web/src/shared/utils/reservation-web/reservation-web-host.ts
 */

export const BRIDGE_SOURCE = 'afterdoc-reservation-bridge'

export type BridgeMessage = {
    source: typeof BRIDGE_SOURCE
    direction: 'to-host' | 'to-sheet'
    channel: string
    payload?: unknown
}

type Listener = (...args: never[]) => void

export type WebIpcBridgeDeps = {
    opener: { postMessage(message: unknown, targetOrigin: string): void }
    allowedOrigins: string[]
    download(fileName: string, data: BlobPart): void
}

const READY_CHANNEL = '__ready'
const ACK_CHANNEL = '__ack'
const INTERNAL_CHANNEL_PREFIX = '__'
// 웹에서는 대응 동작이 없는 채널 (알림 클릭은 React 메인 창이 처리, v1 채팅창은 비범위)
const DROPPED_CHANNELS = new Set(['Application.clickNotification', 'Application.v1ShowChatWindow'])
const FILE_SAVE_CHANNEL = 'Application.openFileSaveDialog'
const FILE_SAVE_RESULT_CHANNEL = 'Application.fileSaveDialogResult'

export const isBridgeMessage = (data: unknown): data is BridgeMessage => {
    if (typeof data !== 'object' || data == null) { return false }
    const message = data as Partial<BridgeMessage>
    return message.source === BRIDGE_SOURCE && typeof message.channel === 'string'
        && (message.direction === 'to-host' || message.direction === 'to-sheet')
}

const toXlsxFileName = (fileName: unknown): string => {
    const name = typeof fileName === 'string' && fileName.length > 0 ? fileName : 'export'
    return name.endsWith('.xlsx') ? name : `${name}.xlsx`
}

export class WebIpcBridge {
    private hostOrigin: string | undefined = undefined
    private readonly listeners = new Map<string, Set<Listener>>()
    private readonly pending: BridgeMessage[] = []

    constructor(private readonly deps: WebIpcBridgeDeps) {
        // 호스트 origin 을 모르므로 허용 origin 각각으로 보낸다 — 불일치 origin 은 브라우저가 폐기한다
        const ready = this.message(READY_CHANNEL)
        deps.allowedOrigins.forEach(origin => deps.opener.postMessage(ready, origin))
    }

    send(channel: string, payload?: unknown): void {
        if (channel === FILE_SAVE_CHANNEL) {
            this.saveFile(payload)
            return
        }
        if (DROPPED_CHANNELS.has(channel)) { return }

        const message = this.message(channel, payload)
        if (this.hostOrigin == undefined) {
            this.pending.push(message)
            return
        }
        this.deps.opener.postMessage(message, this.hostOrigin)
    }

    on(channel: string, listener: Listener): void {
        const set = this.listeners.get(channel) ?? new Set<Listener>()
        set.add(listener)
        this.listeners.set(channel, set)
    }

    removeListener(channel: string, listener: Listener): void {
        this.listeners.get(channel)?.delete(listener)
    }

    removeAllListeners(channel: string): void {
        this.listeners.delete(channel)
    }

    receive(event: { origin: string; data: unknown }): void {
        if (!this.deps.allowedOrigins.includes(event.origin)) { return }
        if (!isBridgeMessage(event.data) || event.data.direction !== 'to-sheet') { return }

        if (this.hostOrigin == undefined) {
            // 호스트 확정은 __ack 로만 한다 — 확정 전 일반 채널 메시지는 버린다
            if (event.data.channel !== ACK_CHANNEL) { return }
            this.hostOrigin = event.origin
            this.flush()
            return
        }
        if (event.origin !== this.hostOrigin) { return }
        if (event.data.channel.startsWith(INTERNAL_CHANNEL_PREFIX)) { return }
        this.emit(event.data.channel, event.data.payload)
    }

    private emit(channel: string, payload: unknown): void {
        // Electron preload 리스너와 같은 (event, payload) 시그니처
        const event = {}
        Array.from(this.listeners.get(channel) ?? []).forEach(listener =>
            (listener as (event: unknown, payload: unknown) => void)(event, payload))
    }

    private flush(): void {
        const hostOrigin = this.hostOrigin
        if (hostOrigin == undefined) { return }
        this.pending.splice(0).forEach(message => this.deps.opener.postMessage(message, hostOrigin))
    }

    private saveFile(payload: unknown): void {
        const { fileName, file } = (payload ?? {}) as { fileName?: unknown; file?: BlobPart }
        const name = toXlsxFileName(fileName)
        // 호출부(ExcelController)는 send 직후에 결과 리스너를 등록하므로 결과 emit 을 microtask 로 미룬다
        queueMicrotask(() => {
            if (file == undefined) {
                console.warn('[WebIpcBridge] openFileSaveDialog called without file')
                this.emit(FILE_SAVE_RESULT_CHANNEL, undefined)
                return
            }
            this.deps.download(name, file)
            this.emit(FILE_SAVE_RESULT_CHANNEL, name)
        })
    }

    private message(channel: string, payload?: unknown): BridgeMessage {
        const message: BridgeMessage = { source: BRIDGE_SOURCE, direction: 'to-host', channel }
        if (payload !== undefined) { message.payload = payload }
        return message
    }
}
